<template>
  <div class="course-exam-page" :class="{ 'is-doing': stage == 'doing' }">
    <!-- 加载中 -->
    <div v-if="loading" v-loading="true" class="exam-state">
      <span>考试题目加载中...</span>
    </div>
    <!-- 加载失败 -->
    <div v-else-if="loadError" class="exam-state">
      <i class="el-icon-warning-outline" style="font-size: 40px; color: #e6a23c;"></i>
      <p>{{ loadError }}</p>
      <el-button type="primary" size="small" @click="loadExam">重新加载</el-button>
    </div>
    <template v-else-if="!loading && !loadError && questions.length">
      <!-- ==================== 阶段一:考试信息概况 + 开始考试 ==================== -->
      <div v-if="stage == 'info'" class="exam-info">
        <div class="paper-title">{{ paperName }}</div>
        <div class="info-list">
          <div class="info-item">
            <div class="info-label">考试时长</div>
            <div class="info-value">{{ durationText }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">题目数量</div>
            <div class="info-value">{{ questionCount }} 题</div>
          </div>
          <div class="info-item">
            <div class="info-label">总分 / 及格分</div>
            <div class="info-value">{{ scoreText }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">可考次数</div>
            <div class="info-value">{{ examCountText }}</div>
          </div>
          <!-- 考试有效期:单独一行整列展示 -->
          <div class="info-item info-item-full" v-if="examDateText">
            <div class="info-label">考试有效期</div>
            <div class="info-value">{{ examDateText }}</div>
          </div>
        </div>
        <div class="info-start">
          <el-button type="primary" size="medium" style="width: 220px;" @click="startExam">开始考试</el-button>
          <!-- 考试前置已签署:由学生自行选择预览承诺书(预览弹窗在父级) -->
          <el-button v-if="needPreCommitment && commitmentSigned" size="medium" style="width: 200px; margin-left: 12px;"
            @click="$emit('previewCommitment')">安全承诺书已签，点击预览</el-button>
        </div>
      </div>
      <!-- ==================== 阶段二:考试答题(左右布局 + 倒计时 + 底部交卷) ==================== -->
      <div v-else class="exam-doing">
        <div class="doing-header">
          <div class="doing-title">
            <el-tag size="mini" type="primary" v-if="submitted">已交卷</el-tag>
            <span class="paper-name">{{ paperName }}</span>
            <!-- <span v-if="submitted" class="submitted-tip">{{ submittedTipText }}</span> -->
          </div>
          <div class="countdown" :class="{ danger: remainSec <= 60 && !submitted }">
            <i class="el-icon-alarm-clock"></i>
            <span class="countdown-label">剩余时间</span>
            <b>{{ fmtTime }}</b>
          </div>
        </div>
        <div class="doing-body">
          <!-- 左侧:当前题目 -->
          <div class="question-panel">
            <template v-if="cur">
              <div class="q-meta">
                <span class="q-pos">第 {{ idx + 1 }} 题 / 共 {{ total }} 题</span>
                <el-tag size="mini">{{ cur.typeText || '未知题型' }}</el-tag>
                <el-tag size="mini" type="warning">{{ cur.score }} 分</el-tag>
              </div>
              <div class="q-title">{{ cur.title }}</div>
              <!-- 题目配图 -->
              <div v-if="cur.pic && cur.pic.indexOf('http') > -1" class="q-pic">
                <el-image
                  v-for="(img, i) in cur.pic.split(',')"
                  :key="i"
                  :src="img"
                  :preview-src-list="cur.pic.split(',')"
                  :initial-index="i"
                  style="width: 120px; height: auto; margin: 0 8px 8px 0;"
                  fit="contain"></el-image>
              </div>
              <!-- 单选 / 判断 -->
              <div v-if="cur.kind == 'radio' || cur.kind == 'judge'" class="q-options">
                <el-radio-group v-model="cur.value" :disabled="submitted">
                  <el-radio v-for="(opt, oi) in cur.options" :key="oi" :label="opt.value" class="q-option">
                    <span class="opt-value">{{ opt.value }}.</span> {{ opt.label }}
                  </el-radio>
                </el-radio-group>
              </div>
              <!-- 多选 -->
              <div v-else-if="cur.kind == 'checkbox'" class="q-options">
                <el-checkbox-group v-model="cur.value" :disabled="submitted">
                  <el-checkbox v-for="(opt, oi) in cur.options" :key="oi" :label="opt.value" class="q-option">
                    <span class="opt-value">{{ opt.value }}.</span> {{ opt.label }}
                  </el-checkbox>
                </el-checkbox-group>
              </div>
              <!-- 填空 / 简答 -->
              <div v-else-if="cur.kind == 'text'" class="q-options">
                <el-input type="textarea" v-model="cur.value" :rows="6" resize="none" :disabled="submitted"
                  placeholder="请输入答案"></el-input>
              </div>
              <!-- 上一题 / 下一题 -->
              <div class="q-switch">
                <el-button size="small" :disabled="idx <= 0" @click="goto(idx - 1)">上一题</el-button>
                <el-button size="small" type="primary" :disabled="idx >= total - 1" @click="goto(idx + 1)">下一题</el-button>
              </div>
              <!-- 题号导航 -->
              <div class="q-palette">
                <div v-for="(q, qi) in questions" :key="qi" class="palette-item"
                  :class="{ answered: isAnswered(q), current: qi == idx }" @click="goto(qi)">
                  {{ qi + 1 }}
                </div>
              </div>
            </template>
          </div>
          <!-- 右侧:考试信息 + 答题进度 -->
          <div class="side-panel">
            <div class="side-block">
              <div class="side-row"><span class="side-label">考试卷名称</span><span class="side-value">{{ paperName }}</span></div>
              <div class="side-row"><span class="side-label">考试时长</span><span class="side-value">{{ durationText }}</span></div>
              <div class="side-row"><span class="side-label">题目数量</span><span class="side-value">{{ questionCount }} 题</span></div>
              <div class="side-row"><span class="side-label">总分 / 及格分</span><span class="side-value">{{ scoreText }}</span></div>
              <div class="side-row"><span class="side-label">可考次数</span><span class="side-value">{{ examCountText }}</span></div>
              <div class="side-row"><span class="side-label">已答 / 未答</span><span class="side-value">{{ answeredCount }} / {{ total - answeredCount }}</span></div>
            </div>
            <div class="side-block">
              <div class="progress-title">答题进度</div>
              <el-progress :percentage="progressPercent" :stroke-width="12" :color="progressColor"></el-progress>
              <div class="progress-num">已答 <b>{{ answeredCount }}</b> 题 / 共 {{ total }} 题</div>
            </div>
          </div>
        </div>
        <!-- 底部固定:提交考试 -->
        <div class="doing-footer">
          <el-button v-if="!submitted" type="primary" size="medium" style="width: 200px;"
            :disabled="stage != 'doing'" @click="submitExam">提交考试</el-button>
          <span v-else class="submitted-text">{{ submittedTipText }}</span>
        </div>
      </div>
    </template>
    <!-- 无题目 -->
    <div v-else class="exam-state">
      <i class="el-icon-document" style="font-size: 40px; color: #909399;"></i>
      <p>未获取到考试题目</p>
      <el-button type="primary" size="small" @click="loadExam">重新加载</el-button>
    </div>
    <!-- 成绩单:交卷成功后展示(成绩=buildPayload.score,结果=提交接口 msg) -->
    <el-dialog title="成绩单" :visible.sync="reportVisible" width="420px" append-to-body
      :close-on-click-modal="false">
      <div class="report-card">
        <div class="report-row">
          <span class="report-label">本次成绩</span>
          <span class="report-value"><b class="report-score">{{ reportScoreText }}</b><span class="report-unit">分</span></span>
        </div>
        <div class="report-row">
          <span class="report-label">考试结果</span>
          <span class="report-value report-result">{{ reportResultText }}</span>
        </div>
      </div>
      <span slot="footer" class="dialog-footer" style="display: block; text-align: center;">
        <el-button type="primary" @click="reportVisible = false">知道了</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'CourseExamPage',
  props: {
    // 试卷 id(调 api/new/paper/detail/{paperId} 获取试卷信息+题目)
    paperId: { type: [Number, String], default: '' },
    // 课程行数据(兜底:course_id / question_name / exam_time 等)
    row: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      stage: 'info', // info:考试信息;doing:答题中
      loading: false,
      loadError: '',
      questions: [],
      total: 0,
      totalScore: 0,
      idx: 0,
      // 倒计时
      remainSec: 0,
      timer: null,
      submitted: false,
      submittedTip: '',
      // 成绩单
      reportVisible: false,
      reportScore: '',
      reportResult: '',
      // api/new/paper/detail 完整响应(试卷元信息 + questions)
      paperData: {},
    };
  },
  computed: {
    cur() {
      return this.questions[this.idx] || null;
    },
    // ==================== 基础信息(直读 api/new/paper/detail 返回字段) ====================
    paperName() {
      return (
        (this.paperData && this.paperData.name) ||
        (this.row && (this.row.question_name || this.row.paper_name)) ||
        '考试'
      );
    },
    passLineNum() {
      // 及格分 pass_line 为字符串("60.00")
      const v = this.paperData && this.paperData.pass_line;
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return isNaN(n) ? v : n;
    },
    passLineText() {
      return this.passLineNum === null ? '—' : this.passLineNum;
    },
    // 总分/及格分展示串(后端已给 "100分 / 60分"),缺失则用题目分值累加兜底
    scoreText() {
      const desc = this.paperData && this.paperData.score_desc;
      if (desc && String(desc).trim()) return String(desc);
      return `${this.totalScore} 分 / ${this.passLineText} 分`;
    },
    questionCount() {
      const c = Number(this.paperData && this.paperData.question_count);
      return c > 0 ? c : this.questions.length;
    },
    durationMin() {
      // 考试时长(分钟):exam_time("60分钟"/"60")
      const raw = (this.paperData && this.paperData.exam_time) || (this.row && this.row.exam_time);
      return this.parseMinutes(raw);
    },
    durationText() {
      // 优先原样展示后端 exam_time("60分钟")
      const raw = (this.paperData && this.paperData.exam_time) || (this.row && this.row.exam_time);
      if (raw && String(raw).trim()) return String(raw);
      const m = this.durationMin;
      if (!m) return '—';
      if (m % 60 === 0) return `${m / 60} 小时`;
      if (m >= 60) return `${Math.floor(m / 60)} 小时 ${m % 60} 分钟`;
      return `${m} 分钟`;
    },
    // 可考次数/本次第几次:后端已给 "本次为第 1 / 3 次"
    examCountText() {
      const desc = (this.paperData && this.paperData.exam_count_desc) || (this.row && this.row.exam_count_desc);
      if (desc && String(desc).trim()) return String(desc);
      const n = this.row && this.row.exam_count;
      return n !== undefined && n !== null && n !== '' ? `${n} 次` : '—';
    },
    examDateText() {
      const p = this.paperData || {};
      const s = p.exam_start_date || (this.row && (this.row.exam_start_date || this.row.start_date)) || '';
      const e = p.exam_end_date || (this.row && (this.row.exam_end_date || this.row.end_date)) || '';
      return s && e ? `${s} 至 ${e}` : '';
    },
    // 考试前置:课程要求"考试前需签署最新版本安全承诺书"(取 api/new/paper/detail 的 course_Info.pre_exam)
    needPreCommitment() {
      const info = (this.paperData && this.paperData.course_Info) || {};
      return info.pre_exam === '考试前需签署最新版本安全承诺书';
    },
    // 该学生是否已签署承诺书(接口返回 is_sign:0 未签署,1 已签署)
    commitmentSigned() {
      return Number(this.paperData && this.paperData.is_sign) == 1;
    },
    answeredCount() {
      return this.questions.filter(q => this.isAnswered(q)).length;
    },
    progressPercent() {
      if (!this.total) return 0;
      return Math.round((this.answeredCount / this.total) * 100);
    },
    progressColor() {
      if (this.progressPercent >= 100) return '#67c23a';
      if (this.progressPercent >= 50) return '#e6a23c';
      return '#f56c6c';
    },
    fmtTime() {
      const s = Math.max(0, this.remainSec);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      const pad = n => String(n).padStart(2, '0');
      return (h > 0 ? pad(h) + ':' : '') + pad(m) + ':' + pad(sec);
    },
    // 已交卷提示:优先取提交接口返回 msg,否则默认文案
    submittedTipText() {
      return (this.submittedTip && String(this.submittedTip).trim()) ? this.submittedTip : '已交卷,可关闭本窗口';
    },
    reportScoreText() {
      if (this.reportScore === '' || this.reportScore === null || this.reportScore === undefined) return '—';
      return this.reportScore;
    },
    reportResultText() {
      return (this.reportResult && String(this.reportResult).trim()) ? this.reportResult : this.submittedTipText;
    },
  },
  created() {
    this.loadExam();
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    // ====================================================================
    // 数据加载:考试页面只用 api/new/paper/detail 一个接口(试卷元信息 + 题目全量返回)
    // ====================================================================
    async loadExam() {
      if (!this.paperId) {
        this.loadError = '缺少试卷ID,无法加载考试题目';
        return;
      }
      this.loading = true;
      this.loadError = '';
      this.stage = 'info';
      try {
        const res = await this.$api.instance.interfaceServer(`api/new/paper/detail/${this.paperId}/${this.row.id}`, 'get',{},{isLoadd:false});
        if (res.code != 0 || !res.data) {
          throw new Error((res && res.msg) || '加载失败');
        }
        this.paperData = res.data;
        const list = Array.isArray(res.data.questions) ? res.data.questions.slice() : [];
        // 题目按绑定排序 pivot.order 升序(同值时保持后端返回顺序)
        list.sort((a, b) => {
          const oa = a.pivot && a.pivot.order ? a.pivot.order : 0;
          const ob = b.pivot && b.pivot.order ? b.pivot.order : 0;
          return oa - ob;
        });
        this.questions = list.map((raw, i) => this.normalizeQuestion(raw, i));
        this.total = this.questions.length;
        this.totalScore = this.questions.reduce((sum, q) => sum + (Number(q.score) || 0), 0);
        if (!this.questions.length) {
          this.loadError = '该试卷暂无考试题目';
        }
      } catch (e) {
        this.loadError = '考试题目加载失败:' + (e.message || '请重试');
      } finally {
        this.loading = false;
      }
    },
    // ====================================================================
    // 题目规范化(待联调:主要核对 raw 字段候选)
    // ====================================================================
    normalizeQuestion(raw, index) {
      const typeText = String(raw.type || raw.type_name || raw.question_type || '');
      const options = this.parseOptions(raw.options_array || raw.options);
      const kind = this.kindOf(typeText, options);
      const q = {
        id: raw.id !== undefined ? raw.id : (raw.question_id !== undefined ? raw.question_id : (raw.paper_question_id !== undefined ? raw.paper_question_id : index)),
        title: raw.name || raw.title || raw.content || raw.question || '',
        typeText,
        kind,
        options: kind == 'judge' && !options.length ? [{ value: '对', label: '对' }, { value: '错', label: '错' }] : options,
        score: Number(raw.score !== undefined ? raw.score : (raw.pivot && raw.pivot.score)) || 0,
        pic: raw.pic || '',
        // 作答值:单选存选项 value(如 A),多选存数组,填空/简答存文本
        value: kind == 'checkbox' ? [] : '',
        origin: raw,
      };
      return q;
    },
    // 题型归类(题库 type 目前为中文:单选/多选/判断/填空/简答等;数字型待联调确认映射)
    kindOf(typeText, options) {
      const t = typeText || '';
      if (t.indexOf('多选') > -1) return 'checkbox';
      if (t.indexOf('判断') > -1) return 'judge';
      if (t.indexOf('填空') > -1 || t.indexOf('问答') > -1 || t.indexOf('简答') > -1 || t.indexOf('论述') > -1 || t.indexOf('作文') > -1) return 'text';
      if (t.indexOf('单选') > -1) return 'radio';
      // 数字型题型兜底(待联调确认):1单选 2多选 3判断 4填空 5简答
      const n = Number(t);
      if (n === 2) return 'checkbox';
      if (n === 3) return 'judge';
      if (n === 4 || n === 5) return 'text';
      if (n === 1) return 'radio';
      if (options && options.length > 1) return 'radio';
      return 'text';
    },
    // 选项解析:options/options_array 可能是 JSON 字符串、'null'、数组
    parseOptions(input) {
      if (input === null || input === undefined || input === '') return [];
      let arr = input;
      if (typeof input === 'string') {
        const s = input.trim();
        if (!s || s == 'null' || s == '[]') return [];
        try {
          arr = JSON.parse(s);
        } catch (e) {
          return [];
        }
      }
      if (!Array.isArray(arr)) return [];
      return arr
        .filter(o => o !== null && o !== undefined)
        .map(o => {
          if (o && typeof o === 'object') {
            const value = o.value !== undefined && o.value !== null ? o.value : o.label;
            return { value: value !== undefined ? value : '', label: o.label !== undefined ? o.label : value };
          }
          return { value: String(o), label: String(o) };
        });
    },
    isAnswered(q) {
      if (!q) return false;
      if (q.kind == 'checkbox') return !!(q.value && q.value.length);
      if (q.kind == 'text') return !!(q.value && String(q.value).trim());
      return q.value !== '' && q.value !== null && q.value !== undefined;
    },
    // ====================================================================
    // 开始考试 / 倒计时
    // ====================================================================
    parseMinutes(raw) {
      if (raw === null || raw === undefined || raw === '') return 0;
      const s = String(raw);
      // "hh:mm:ss" / "mm:ss"
      if (s.indexOf(':') > -1) {
        const p = s.split(':').map(n => Number(n) || 0);
        if (p.length === 3) return p[0] * 60 + p[1] + p[2] / 60;
        return p[0] + p[1] / 60;
      }
      const n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    },
    startExam() {
      if (!this.questions.length || this.submitted) return;
      // 考试时长(分钟)来源:api/new/paper/detail 的 exam_time("60分钟"/"60"),缺失兜底 30 分钟
      const m = this.durationMin || 30;
      this.remainSec = Math.round(m * 60);
      this.idx = 0;
      this.stage = 'doing';
      this.clearTimer();
      this.timer = setInterval(() => {
        if (this.submitted) return;
        if (this.remainSec <= 1) {
          this.handleTimeUp();
          return;
        }
        this.remainSec--;
      }, 1000);
    },
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // ====================================================================
    // 答题交互
    // ====================================================================
    goto(index) {
      if (index >= 0 && index < this.total) {
        this.idx = index;
      }
    },
    // ====================================================================
    // 交卷
    // ====================================================================
    // 手动点“提交考试”:确认(含未答提醒)后交卷
    submitExam() {
      if (this.submitted) return;
      const answered = this.answeredCount;
      const remain = this.total - answered;
      const confirmMsg = remain > 0
        ? `共 ${this.total} 题,已答 ${answered} 题,还有 ${remain} 题未答,确定交卷吗?`
        : `共 ${this.total} 题已全部作答,确定交卷吗?`;
      this.$confirm(confirmMsg, '提交考试', {
        confirmButtonText: '确定交卷',
        cancelButtonText: '取消',
        type: remain > 0 ? 'warning' : 'info',
      })
        .then(() => this.submitNow())
        .catch(() => {});
    },
    // 时间到:锁定时间并提示“提交考试”(不可取消,任何关闭动作都交卷)
    handleTimeUp() {
      if (this.submitted) return;
      this.clearTimer();
      this.remainSec = 0;
      this.$confirm('考试时间已到,请提交考试!', '提交考试', {
        confirmButtonText: '提交考试',
        showCancelButton: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        type: 'warning',
      })
        .then(() => this.submitNow())
        .catch(() => this.submitNow());
    },
    // 实际交卷(仅一次):置已交卷 -> 停表 -> 通知父级提交
    submitNow() {
      if (this.submitted) return;
      this.submitted = true;
      this.clearTimer();
      this.$emit('submit', this.buildPayload());
    },
    // 中途退出:算作一次考试,确认后提交当前试卷(由父级 before-close 触发)
    quitSubmit() {
      if (this.submitted) return false;
      this.submitted = true;
      this.clearTimer();
      this.$emit('submit', this.buildPayload());
      return true;
    },
    // 供父级判断是否已交卷(已交卷时允许直接关闭弹窗)
    getSubmitted() {
      return this.submitted;
    },
    // 提交接口成功后由父级回填提示文案(res.msg)
    setSubmittedTip(msg) {
      if (msg && String(msg).trim()) {
        this.submittedTip = String(msg);
      }
    },
    // 交卷成功后弹出成绩单:成绩取本次提交分数(buildPayload.score),结果取提交接口 msg(submittedTip)
    showReport(score, result) {
      this.reportScore = (score === undefined || score === null || score === '') ? '' : score;
      this.reportResult = (result && String(result).trim()) ? String(result) : '';
      this.reportVisible = true;
    },
    // 交卷数据组装:api/new/paper/submit POST
    // { id: 试卷id, course_id, score: 前端判分合计, content: 题目数组JSON字符串 }
    buildPayload() {
      const contentList = [];
      let score = 0;
      this.questions.forEach(q => {
        const origin = q.origin || {};
        const answerArr = this.parseAnswerArr(origin.answers);
        if (this.judgeQuestion(q, answerArr)) {
          score += Number(q.score) || 0;
        }
        contentList.push({
          type: q.typeText,
          name: q.title,
          // 多选存数组(原样),单选/判断/填空/简答存字符串
          value: q.kind == 'checkbox' ? (Array.isArray(q.value) ? q.value.slice() : []) : (q.value || ''),
          answers: origin.answers === null || origin.answers === undefined ? '' : String(origin.answers),
          score: q.score,
          options: q.options,
          pic: origin.pic || null,
        });
      });
      return {
        id: Number(this.paperId) || this.paperId,
        course_id: (this.row && this.row.id) || '',
        score,
        content: JSON.stringify(contentList),
      };
    },
    // 解析正确答案字符串:answers 为 JSON 数组字符串,如 '["A", "B"]'(可能带空格)
    parseAnswerArr(answers) {
      if (answers === null || answers === undefined || answers === '') return [];
      try {
        const v = JSON.parse(String(answers));
        return Array.isArray(v) ? v : [];
      } catch (e) {
        return [];
      }
    },
    // 前端判分:与正确答案集合完全一致才得分(填空/简答不自动判分)
    judgeQuestion(q, answerArr) {
      if (!q || !answerArr || !answerArr.length) return false;
      if (q.kind == 'text') return false;
      const mine = q.kind == 'checkbox'
        ? (Array.isArray(q.value) ? q.value : [])
        : (q.value === '' || q.value === null || q.value === undefined ? [] : [q.value]);
      if (!mine.length) return false;
      const normalize = list => list.map(String).map(s => s.trim()).sort();
      const a = normalize(mine).join(',');
      const b = normalize(answerArr).join(',');
      return a === b;
    },
    // 供父级弹窗 before-close 判断当前是否答题中
    getStage() {
      return this.stage;
    },
  },
};
</script>

<style lang="less" scoped>
.course-exam-page {
  width: 100%;
  box-sizing: border-box;

  &.is-doing {
    display: flex;
    flex-direction: column;
    height: 620px;
  }
}

// 加载中 / 错误 / 空
.exam-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  color: #606266;

  p {
    margin: 12px 0 16px;
    font-size: 14px;
  }
}

// ---------- 阶段一:考试信息概况 ----------
.exam-info {
  padding: 18px 24px 10px;
}

.paper-title {
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: #303133;
  padding: 8px 0 20px;
  word-break: break-all;
}

.info-list {
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid #ebeef5;
  border-left: 1px solid #ebeef5;

  .info-item {
    width: 50%;
    box-sizing: border-box;
    border-right: 1px solid #ebeef5;
    border-bottom: 1px solid #ebeef5;
    padding: 16px 20px;
    text-align: center;

    // 考试有效期:独占一整行
    &.info-item-full {
      width: 100%;
    }

    .info-label {
      font-size: 13px;
      color: #909399;
      margin-bottom: 10px;
    }

    .info-value {
      font-size: 16px;
      font-weight: bold;
      color: #303133;
      word-break: break-all;
    }
  }
}

.info-start {
  text-align: center;
  padding: 26px 0 8px;
}

// ---------- 阶段二:答题 ----------
.exam-doing {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.doing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 54px;
  flex: none;
  padding: 0 16px;
  border-bottom: 1px solid #ebeef5;

  .doing-title {
    display: flex;
    align-items: center;
    min-width: 0;

    .paper-name {
      font-size: 16px;
      font-weight: bold;
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-left: 8px;
    }

    .submitted-tip {
      margin-left: 12px;
      font-size: 12px;
      color: #909399;
    }
  }

  .countdown {
    display: flex;
    align-items: center;
    flex: none;
    font-size: 14px;
    color: #303133;
    padding: 4px 14px;
    border-radius: 4px;
    background: #fdf6ec;

    i {
      color: #e6a23c;
      margin-right: 6px;
    }

    .countdown-label {
      color: #909399;
      margin-right: 8px;
    }

    b {
      font-size: 18px;
      color: #e6a23c;
      font-variant-numeric: tabular-nums;
    }

    &.danger {
      background: #fef0f0;

      b {
        color: #f56c6c;
        animation: blink 1s infinite;
      }
    }
  }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

.doing-body {
  flex: 1;
  min-height: 0;
  display: flex;
  padding: 14px 16px;
  box-sizing: border-box;
}

// 左侧题目
.question-panel {
  flex: 1;
  min-width: 0;
  margin-right: 14px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;
  box-sizing: border-box;
  background: #fff;

  .q-meta {
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .q-pos {
      font-size: 15px;
      font-weight: bold;
      color: #303133;
      margin-right: 12px;
    }

    .el-tag {
      margin-right: 8px;
    }
  }

  .q-title {
    font-size: 15px;
    line-height: 1.6;
    color: #303133;
    white-space: pre-wrap;
    word-break: break-all;
    margin-bottom: 10px;
  }

  .q-pic {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  .q-options {
    .q-option {
      display: flex;
      align-items: flex-start;
      margin: 8px 0;
      padding: 8px 10px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      height: auto;
      white-space: normal;
      line-height: 1.5;

      .opt-value {
        font-weight: bold;
        margin-right: 4px;
      }
    }

    /deep/.el-radio__label,
    /deep/.el-checkbox__label {
      white-space: normal;
      line-height: 1.5;
      word-break: break-all;
    }
  }

  .q-switch {
    margin-top: 16px;
    text-align: center;
  }

  .q-palette {
    display: flex;
    flex-wrap: wrap;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px dashed #ebeef5;

    .palette-item {
      width: 30px;
      height: 30px;
      line-height: 30px;
      text-align: center;
      font-size: 13px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      color: #606266;
      background: #f5f7fa;
      cursor: pointer;
      margin: 0 6px 8px 0;
      box-sizing: border-box;
      user-select: none;

      &.answered {
        background: #67c23a;
        border-color: #67c23a;
        color: #fff;
      }

      &.current {
        border: 2px solid #409eff;
        color: #409eff;
        font-weight: bold;
        line-height: 26px;
      }

      &.answered.current {
        background: #409eff;
        border-color: #409eff;
        color: #fff;
        line-height: 26px;
      }
    }
  }
}

// 右侧考试信息
.side-panel {
  width: 268px;
  flex: none;
  overflow-y: auto;

  .side-block {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 14px;
    background: #fff;

    & + .side-block {
      margin-top: 14px;
    }

    .side-row {
      display: flex;
      padding: 7px 0;
      font-size: 13px;

      .side-label {
        width: 86px;
        flex: none;
        color: #909399;
      }

      .side-value {
        flex: 1;
        min-width: 0;
        color: #303133;
        word-break: break-all;
      }
    }

    .progress-title {
      font-size: 14px;
      font-weight: bold;
      color: #303133;
      margin-bottom: 12px;
    }

    .progress-num {
      margin-top: 10px;
      font-size: 13px;
      color: #909399;
      text-align: right;

      b {
        color: #409eff;
        font-size: 16px;
      }
    }
  }
}

// 底部固定交卷
.doing-footer {
  flex: none;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid #ebeef5;
  background: #fff;

  .submitted-text {
    font-size: 13px;
    color: #909399;
  }
}

// ---------- 成绩单弹窗 ----------
.report-card {
  padding: 10px 4px;

  .report-row {
    display: flex;
    align-items: center;
    padding: 12px 0;

    & + .report-row {
      border-top: 1px dashed #ebeef5;
    }

    .report-label {
      width: 90px;
      flex: none;
      font-size: 14px;
      color: #909399;
    }

    .report-value {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: baseline;
      font-size: 14px;
      color: #303133;
      word-break: break-all;

      .report-score {
        font-size: 42px;
        font-weight: bold;
        color: #409eff;
        font-variant-numeric: tabular-nums;
      }

      .report-unit {
        margin-left: 6px;
        font-size: 16px;
        color: #606266;
      }

      &.report-result {
        line-height: 1.6;
      }
    }
  }
}
</style>
